import { useLingui } from '@lingui/react/macro'
import { useLocation } from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import type { WorkspaceSettingsNavigateHandler } from 'features/navbar/configSettings/ConfigSettingsSidebarMenuItems'
import { useGeckoPermissions } from 'features/permissions'
import { useTermsModal } from 'features/terms'
import mixpanel from 'mixpanel-browser'
import { useState } from 'react'

export type ProfileAndSettingsProps = {
  orgID?: string
  siteID?: string
  onWorkspaceNavigate?: WorkspaceSettingsNavigateHandler
  onOpenNotificationPreferences: () => void
  onOpenUploadDocuments?: () => void
  showNotificationPreferences?: boolean
}

const getUserDisplayName = (
  user: { name?: string; email?: string } | undefined
): string | undefined => {
  const name = user?.name?.trim()
  if (name) return name

  const email = user?.email?.trim()
  if (email) return email

  return undefined
}

export const useProfileAndSettingsState = () => {
  const { t } = useLingui()
  const location = useLocation()
  const { setShowToSModal } = useTermsModal()
  const { logout } = useGeckoAuth()
  const { user } = useGeckoPermissions()
  const { BASE_PATH } = useRuntimeVariables()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const userDisplayName = getUserDisplayName(user) ?? t`User Name`
  const shouldShowUnits =
    location.pathname.includes('/details') ||
    location.pathname.includes('/assets') ||
    location.pathname.includes('/inspections') ||
    location.pathname.includes('/scheduling') ||
    location.pathname.includes('/configuration') ||
    location.pathname.includes('/viewer') ||
    location.pathname.includes('/reporting') ||
    location.pathname.includes('/libra')

  const handleLogOut = () => {
    mixpanel.reset()
    logout({
      returnTo: `${window.location.origin}${BASE_PATH}`,
    })
  }

  return {
    handleLogOut,
    isModalOpen,
    setIsModalOpen,
    setShowToSModal,
    shouldShowUnits,
    userDisplayName,
  }
}

export type ProfileAndSettingsState = ReturnType<
  typeof useProfileAndSettingsState
>
